import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Text, Button } from "@chakra-ui/react";
import { routes } from "~/constants/routes";
import { ProductLogo } from "~/DiagnosticsApp/components";
import type { LabTest } from "sdk/labTests";

interface Props {
  labTest: LabTest;
  userName: string;
}

export const LabTestCard: FC<Props> = ({ labTest, userName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isCompleted = labTest.status === "completed";
  const showConsultationButton = isCompleted && !labTest.consultation;
  const scheduleTranslationKey =
    labTest.provider_type === "legacy_health"
      ? "diagnosticDashboard.consultation.scheduleNurse"
      : "diagnosticDashboard.consultation.scheduleDoctor";

  const handleBookConsultation = () => {
    if (labTest.provider_type === "legacy_health") {
      window.location.href = "https://conceive.as.me/conceive-assess";
      return;
    }

    const diagnosticUserProductId = labTest.diagnostic_user_product_id;
    navigate(
      routes.diagnosticsIntake.replace(
        ":diagnostic_user_product_id",
        diagnosticUserProductId.toString(),
      ) + `?lab_test_id=${labTest.id}`,
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const StatusLabel = () => {
    const statusConfig = {
      completed: {
        bg: "th.yellowGreen1",
        color: "th.signatureGreen",
        borderColor: "th.signatureGreen",
        text: "Results ready",
        icon: "check",
      },
      default: {
        bg: "th.yellow1",
        color: "th.black",
        borderColor: "th.black",
        text:
          labTest.provider_type === "legacy_health"
            ? "Take your test"
            : "Schedule your test",
        icon: "clock",
      },
    };

    const config =
      (labTest.status &&
        statusConfig[labTest.status as keyof typeof statusConfig]) ||
      statusConfig.default;

    return (
      <Box position="relative" display="inline-flex">
        {/* Main content with angled edges using clip-path */}
        <Flex
          align="center"
          gap={2}
          bg={config.bg}
          px={6}
          py="5px"
          position="relative"
          sx={{
            clipPath: "polygon(16px 0, 100% 0, calc(100% - 16px) 100%, 0 100%)",
          }}
        >
          {config.icon &&
            (config.icon === "check" ? (
              <Flex
                align="center"
                justify="center"
                boxSize="20px"
                borderRadius="full"
                border="1.75px solid"
                borderColor={config.borderColor}
              >
                <Box
                  as="svg"
                  width="10px"
                  height="8px"
                  viewBox="0 0 10 8"
                  fill="none"
                >
                  <path
                    d="M1 4L3.5 6.5L9 1"
                    stroke={
                      config.color === "th.signatureGreen"
                        ? "#1A5544"
                        : "currentColor"
                    }
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Box>
              </Flex>
            ) : (
              <Box
                as="svg"
                width="20px"
                height="20px"
                viewBox="0 0 20 20"
                fill="none"
              >
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke={
                    config.color === "th.black" ? "#111A18" : "currentColor"
                  }
                  strokeWidth="2"
                />
                <path
                  d="M10 6V10L13 13"
                  stroke={
                    config.color === "th.black" ? "#111A18" : "currentColor"
                  }
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Box>
            ))}
          <Text
            fontSize="16px"
            fontWeight="bold"
            color={config.color}
            lineHeight="1.4"
          >
            {config.text}
          </Text>
        </Flex>
      </Box>
    );
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      boxShadow="0px 5px 20px 0px rgba(0, 0, 0, 0.05)"
      p={4}
      border="1px solid"
      borderColor="th.gray1"
      position="relative"
    >
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align="start"
        gap={4}
      >
        <Flex direction="column" gap="14px">
          <Flex
            align="start"
            direction={{ base: "column", md: "row" }}
            gap="14px"
          >
            <ProductLogo
              slug={labTest.diagnostic_product.slug}
              boxSize="45px"
            />
            <Flex direction="column" gap={0}>
              <Text
                fontSize="18px"
                fontWeight="bold"
                color="th.black"
                lineHeight="1.4"
              >
                {labTest.name}
              </Text>
              <Text
                fontSize="18px"
                fontWeight="normal"
                color="th.black"
                lineHeight="1.4"
              >
                {userName}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            <Text
              fontSize="16px"
              fontWeight="normal"
              color="th.black"
              lineHeight="1.4"
            >
              {isCompleted
                ? `Results received on: ${formatDate(labTest.completed_at)}`
                : `Purchased on: ${formatDate(labTest.created_at)}`}
            </Text>
            {/* <Text
              fontSize="16px"
              fontWeight="normal"
              color="th.gray4"
              lineHeight="1.4"
            >
              Test ID#: {labTest.id}
            </Text> */}
          </Flex>
        </Flex>

        <Flex
          justify="space-between"
          direction="column"
          gap={{ base: 2, md: 8 }}
        >
          <StatusLabel />
          <Flex justify="start" direction="column" gap={{ base: 1 }}>
            {isCompleted && (
              <Button
                as="a"
                href={labTest.result_download_url || "#"}
                target={labTest.result_download_url ? "_blank" : "_self"}
                rel={labTest.result_download_url ? "noopener noreferrer" : ""}
                bg="th.signatureGreen"
                color="white"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="1.4"
                px={6}
                py={4}
                height="auto"
                borderRadius="xl"
                _hover={{ bg: "newDarkGreen.700" }}
              >
                {t("diagnosticDashboard.consultation.viewResults")}
              </Button>
            )}
            {showConsultationButton && (
              <Button
                onClick={handleBookConsultation}
                bg="white"
                color="th.signatureGreen"
                border="2px solid"
                borderColor="th.signatureGreen"
                fontSize="14px"
                fontWeight="bold"
                lineHeight="1.4"
                px={6}
                py={4}
                height="auto"
                borderRadius="xl"
                _hover={{ bg: "th.yellowGreen1" }}
              >
                {t(scheduleTranslationKey)}
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
